import { CompoundModGroupSlotType, CompoundModGroupType, CompoundModGroupTypeHelper, ModGroupDisplayStyle, UserRight, } from '@biteinc/enums';
import { AbstractModelSchema } from './abstract_model_schema.js';
export const compoundModGroupSchema = {
    displayName: 'compound modifier group',
    type: 'object',
    fields: {
        ...AbstractModelSchema.fields,
        orgId: {
            type: 'mongoId',
            required: true,
            isHidden: true,
            isReadOnly: true,
        },
        siteId: {
            type: 'mongoId',
            required: true,
            isHidden: true,
            isReadOnly: true,
        },
        locationId: {
            type: 'mongoId',
            required: true,
            isHidden: true,
            isReadOnly: true,
        },
        vendorId: {
            displayName: 'Vendor',
            type: 'mongoId',
            ui: 'dropdown',
            required: true,
            writeRight: UserRight.ManageMenu,
        },
        type: {
            type: 'stringEnum',
            enum: CompoundModGroupType,
            enumNameHelper: CompoundModGroupTypeHelper.name,
            ui: 'dropdown',
            required: true,
            isPermanent: true,
            writeRight: UserRight.ManageMenu,
        },
        name: {
            type: 'shortString',
            required: true,
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        internalName: {
            displayName: 'Internal Name',
            type: 'shortString',
            collapse: true,
            tooltip: {
                text: 'A name used only to identify this compound mod group internally. Not seen by guests.',
            },
            writeRight: UserRight.ManageMenu,
        },
        magicCopyKey: {
            type: 'keyString',
            required: true,
            isHidden: true,
            isReadOnly: true,
        },
        description: {
            type: 'longString',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        hideFromMenu: {
            displayName: 'Hide from Menu',
            type: 'bool',
            writeRight: UserRight.ManageMenu,
        },
        columnCount: {
            type: 'int',
            displayName: 'Column Count',
            min: 1,
            max: 3,
            defaultValue: null,
            required: true,
            isNullable: true,
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'Integer from 1 to 3. If null, will use the default setting from the location.',
            },
        },
        prependPlusSignBeforeModPrices: {
            type: 'bool',
            displayName: 'Prepend Plus Sign Before Mod Prices',
            defaultValue: null,
            required: true,
            isNullable: true,
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'If null, will use the default setting from the location.',
            },
        },
        showAsCollapsed: {
            displayName: 'Show in collapsed state in Menu',
            type: 'bool',
            writeRight: UserRight.ManageMenu,
        },
        deemphasize: {
            displayName: 'De-emphasize in Menu',
            type: 'bool',
            writeRight: UserRight.ManageMenu,
        },
        displayStyle: {
            displayName: 'Display Style',
            type: 'stringEnum',
            required: true,
            enum: ModGroupDisplayStyle,
            values: [ModGroupDisplayStyle.RowSizeGroup],
            ui: 'dropdown',
        },
        images: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 5,
            keepAspectRatio: 'fit-width',
            size: {
                // 4:1 ratio
                width: 1080,
                height: 270,
            },
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        modGroupIds: {
            displayName: 'Mod Groups',
            type: 'array',
            elementType: 'mongoId',
            required: true,
            minCount: 1,
            writeRight: UserRight.ManageMenu,
            placeholder: 'Add Mod Group',
            ui: 'table',
        },
        removeRegularExtraMods: {
            displayName: 'Mods',
            type: 'array',
            elementType: 'object',
            required: true,
            minCount: 1,
            conditions: [
                {
                    field: 'type',
                    values: [CompoundModGroupType.MergeRemoveRegularExtraMods],
                },
            ],
            fields: {
                _id: {
                    type: 'mongoId',
                    isReadOnly: true,
                    required: true,
                    isHidden: true,
                },
                name: {
                    type: 'shortString',
                    required: true,
                    canHaveLocalizationOverrides: true,
                    writeRight: UserRight.ManageMenu,
                },
                images: {
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'Image',
                    elementUniqueIdFieldName: 'origUrl',
                    maxCount: 1,
                    keepAspectRatio: 'contain',
                    size: {
                        width: 300,
                        height: 200,
                    },
                    jpgSize: {
                        width: 600,
                        height: 400,
                    },
                    canHaveLocalizationOverrides: true,
                    writeRight: UserRight.ManageMenu,
                },
                removeSlot: getSlotSchema('Remove'),
                regularSlot: getSlotSchema('Regular', [
                    CompoundModGroupSlotType.Placeholder,
                    CompoundModGroupSlotType.Real,
                ]),
                extraSlot: getSlotSchema('Extra', [
                    CompoundModGroupSlotType.Real,
                    CompoundModGroupSlotType.Empty,
                ]),
                invalidReason: {
                    displayName: 'Invalid Reason',
                    type: 'shortString',
                    isReadOnly: true,
                    isNullable: true,
                    required: true,
                    placeholder: 'Mods are Valid',
                },
            },
            writeRight: UserRight.ManageMenu,
            ui: 'accordion',
            uiSubtype: 'CompoundModGroupMods',
        },
    },
};
function getSlotSchema(displayName, allowedSlotTypes = []) {
    return {
        type: 'object',
        required: true,
        displayName,
        uiSubtype: 'CompoundModGroupSlot',
        fields: {
            slotType: {
                displayName: 'Slot Type',
                type: 'stringEnum',
                enum: CompoundModGroupSlotType,
                ...(allowedSlotTypes.length && { values: allowedSlotTypes }),
                required: true,
            },
            _id: {
                type: 'mongoId',
                isReadOnly: true,
                required: true,
                isHidden: true,
                conditions: [
                    {
                        field: 'slotType',
                        values: [CompoundModGroupSlotType.Placeholder],
                    },
                ],
            },
            name: {
                displayName: 'Name',
                type: 'shortString',
                required: true,
                canHaveLocalizationOverrides: true,
                conditions: [
                    {
                        field: 'slotType',
                        values: [CompoundModGroupSlotType.Placeholder],
                    },
                ],
            },
            modGroupId: {
                displayName: 'Mod Group',
                type: 'mongoId',
                required: true,
                conditions: [
                    {
                        field: 'slotType',
                        values: [CompoundModGroupSlotType.Real],
                    },
                ],
                ui: 'dropdown',
            },
            modId: {
                displayName: 'Mod',
                type: 'mongoId',
                required: true,
                conditions: [
                    {
                        field: 'slotType',
                        values: [CompoundModGroupSlotType.Real],
                    },
                ],
                ui: 'dropdown',
            },
        },
    };
}
//# sourceMappingURL=compound_mod_group_schema.js.map